import { useMemo } from 'react'
import styled from 'styled-components'
import { ProductsListItemResponseDto } from '@/apis/rest/generated/types'
import { useBackAction } from '@/components/common/back-action/use-back-action'
import { JustViewedProductList } from '@/components/domains/list/just-viewed-product-list'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import SkeletonProduct from '@/components/domains/products/skeleton/SkeletonProduct'
import { ProductSkeletonLengthEnum } from '@/constants/product-skeleton-length.enum'
import { ProductThumbnailSizeEnum } from '@/constants/product-thumbnail-size.enum'
import { useTracker } from '@/containers/contexts/EventTrackingProvider'
import useCategoriesProductListV2 from '@/containers/hooks/categories/productsList/rootCategories/productsListV2.hook'
import { TrackingInfoType } from '@/types/event-tracker.type'
import { convertProductCardParams } from '@/utils/event-tracker/convertParams/convertProductParams'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { unset } from '@/utils/utilCommon'
import type { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'

type ListProductsProps = {
  trackingInfo?: TrackingInfoType
  currentCategoryId: string
}

export const ListProductsV2 = ({ currentCategoryId, trackingInfo }: ListProductsProps) => {
  const tracker = useTracker()
  const backAction = useBackAction('recommend-list-for-category-list')

  const { ref, productListData, isLoading } = useCategoriesProductListV2({
    categoryId: currentCategoryId,
  })

  const handleClickProductCard = ({
    item,
    properties,
  }: {
    item: ProductTypeDefinedByFrontend
    index: number
    properties?: Record<string, any>
  }) => {
    if (!trackingInfo) {
      return
    }

    const _trackingInfo = trackingInfo

    unset(_trackingInfo, _trackingInfo.eventName)
    tracker.triggerCustomEvent(trackingInfo.eventName, {
      ..._trackingInfo,
      ...convertProductCardParams(item),
      ...properties,
    })
  }

  const memoizedGroupByFlattedProductList = useMemo(() => {
    const paged = productListData ? productListData.pages.map((page) => page.data || []) : []
    const flatted = paged.flatMap((product) => product)
    return flatted.reduce<ProductsListItemResponseDto[][]>((acc, cur, idx, arr) => {
      if (idx % 2 === 0) return [...acc, ...[arr.slice(idx, idx + 2)]]
      return acc
    }, [])
  }, [productListData])

  return (
    <>
      {!isLoading ? (
        <StyledRootDiv>
          {memoizedGroupByFlattedProductList.map((list, groupedIndex) => {
            const queue = backAction.front()
            const isShowJustViewedProduct = list.some((p) => backAction.contain(`${groupedIndex}::${p.id}`))
            const justViewedProduct = list.find((p) => queue?.condition?.includes(p.id.toString()))

            return (
              <StyledGroupDiv key={groupedIndex}>
                <StyledListLayout>
                  {list.map((data, index) => {
                    const product = transformProductTypeDefinedByFrontend(data)

                    return (
                      <div key={product.id} ref={ref}>
                        <NewProductCard
                          product={product}
                          size={PRODUCT_CARD_SIZES.half}
                          rankingNumber={index}
                          hasCartButton
                          productCardTrackerData={{ location: 'category' }}
                          onClickProductCard={() => {
                            backAction.push(`${groupedIndex.toString()}::${product.id}`)
                            handleClickProductCard({ item: product, index })
                          }}
                        />
                      </div>
                    )
                  })}
                </StyledListLayout>
                {isShowJustViewedProduct && justViewedProduct && (
                  <JustViewedProductList
                    name="recommend-list-for-category-list"
                    justViewedProduct={transformProductTypeDefinedByFrontend(justViewedProduct)}
                    onClickProductCardTracker={handleClickProductCard}
                    trackerData={{
                      location: 'category',
                      addLocation: 'category',
                      categoryName: trackingInfo?.categoryName as string,
                      categoryTypeName: trackingInfo?.categoryTypeName as string,
                    }}
                  />
                )}
              </StyledGroupDiv>
            )
          })}
        </StyledRootDiv>
      ) : (
        <StyledSkeleton>
          <SkeletonProduct length={ProductSkeletonLengthEnum.Eight} size={ProductThumbnailSizeEnum.Fill} />
        </StyledSkeleton>
      )}
    </>
  )
}

const StyledListLayout = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: repeat(2, 1fr);
  grid-row-gap: 3.2rem;
  grid-column-gap: 0.8rem;
  padding: 0 1.6rem;
`

const StyledSkeleton = styled(StyledListLayout)`
  & > div {
    &:nth-child(1),
    &:nth-child(2) {
      margin-top: 0;
    }
  }
`

const StyledRootDiv = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 3.2rem;
`

const StyledGroupDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
`
